import * as React from 'react'
import { mdiLockOutline, mdiMapMarkerQuestionOutline, mdiAlertCircleOutline } from '@mdi/js'
import { Box, Container, Flex, Heading, Image, Text } from '@chakra-ui/react'

export interface GenericErrorTemplateProps {
  /** The title of the page */
  title: string
  /** Optional subtext of the page */
  subtext?: string
  /** Main error text */
  text: React.ReactNode | string
  /** Links */
  shortcuts?: React.ReactNode
  /** Variant types */
  variant: 'not-found' | 'access-denied' | 'error' | 'unauthorized'
}

export const variants = {
  'not-found': {
    color: 'purple',
    icon: mdiMapMarkerQuestionOutline,
    illustration: {
      colored: 'https://delivery-sitecore.sitecorecontenthub.cloud/api/public/content/spot-map-search',
      grayed: 'https://delivery-sitecore.sitecorecontenthub.cloud/api/public/content/spot-map-search-neutral'
    },
    label: 'Resource not found'
  },
  'access-denied': {
    color: 'orange',
    icon: mdiLockOutline,
    illustration: {
      colored: 'https://delivery-sitecore.sitecorecontenthub.cloud/api/public/content/spot-lock',
      grayed: 'https://delivery-sitecore.sitecorecontenthub.cloud/api/public/content/spot-lock-neutral'
    },
    label: 'Access denied'
  },
  error: {
    color: 'red',
    icon: mdiAlertCircleOutline,
    illustration: {
      colored: 'https://delivery-sitecore.sitecorecontenthub.cloud/api/public/content/spot-alert',
      grayed: 'https://delivery-sitecore.sitecorecontenthub.cloud/api/public/content/spot-alert-neutral'
    },
    label: 'Error'
  },
  unauthorized: {
    color: 'red',
    icon: mdiAlertCircleOutline,
    illustration: {
      colored: 'https://delivery-sitecore.sitecorecontenthub.cloud/api/public/content/spot-stop',
      grayed: 'https://delivery-sitecore.sitecorecontenthub.cloud/api/public/content/spot-stop-neutral'
    },
    label: 'Unauthorized'
  },
  unavailable: {
    color: 'red',
    icon: mdiAlertCircleOutline,
    illustration: {
      colored: 'https://delivery-sitecore.sitecorecontenthub.cloud/api/public/content/spot-wrench-clock',
      grayed: 'https://delivery-sitecore.sitecorecontenthub.cloud/api/public/content/spot-wrench-clock-neutral'
    },
    label: 'Error'
  }
}

const GenericErrorTemplate: React.FC<GenericErrorTemplateProps> = ({ title, subtext, text, variant, shortcuts }) => {
  const activeVariant = variants[variant]

  return (
    <Container pt={9} py={8} pb={12} height='100%' width='100%'>
      <Container maxW='sm' width='sm' pt={24}>
        <Flex justify='center' flexDirection='column' alignItems='center'>
          <Box borderRadius='2xl' display='flex' alignItems='center' justifyContent='center'>
            <Image width={28} height={28} src={activeVariant.illustration.colored} alt={activeVariant.label} />
          </Box>
          <Heading textAlign='center' pt='8' size='xl' as='h2'>
            {title}
          </Heading>
          <Text textAlign='center' variant='subtle' pt='2'>
            {subtext}
          </Text>
          <Text textAlign='center' py='6' size='md' color='gray.800'>
            {text}
          </Text>
          {shortcuts && (
            <Box display='flex' flexDirection='column' alignItems='center'>
              {shortcuts}
            </Box>
          )}
        </Flex>
      </Container>
    </Container>
  )
}

export { GenericErrorTemplate }
